/**
 * TrustCompoundingGraph.jsx — GRANULLO_TRUST_COMPOUNDING_GRAPH_CLARITY_REBUILD_V1
 *                           + GRANULLO_STRUCTURE_THEOREM_GRAPH_REBUILD_V1
 *
 * MERGED CHAPTER: The Economics of Proof
 *
 * TOP: Split-slope theorem graph
 *   LEFT = Cost of uncertainty (rising red cost line)
 *   CENTER = First Memory Lock (chalk circle inflection)
 *   RIGHT = Infrastructure mode (confidence rises, cost falls)
 *
 * BOTTOM: 3-step handwritten narrative rail
 *
 * No inline text on chart. Canvas stays clean.
 */
import React, { useRef, useEffect, useState } from 'react';
import { motion, useInView } from 'framer-motion';

const RED = '#CD2200';
const AMBER = '#D97706';
const GREEN = '#22C55E';
const GREY = '#555555';
const BG = '#050505';

/* ═══ GRAPH DIMENSIONS ═══ */
const W = 680;
const H = 300;
const PAD = { left: 48, right: 32, top: 36, bottom: 44 };
const PW = W - PAD.left - PAD.right;
const PH = H - PAD.top - PAD.bottom;

/* Inflection at 35% of X axis */
const IX = PAD.left + PW * 0.35;
const IY = PAD.top + PH * 0.5;

/* ═══ PATHS ═══ */

/* Cost of uncertainty: rises left to center */
const costBeforePath = `M ${PAD.left} ${PAD.top + PH * 0.75}
  C ${PAD.left + PW * 0.12} ${PAD.top + PH * 0.72},
    ${IX - PW * 0.08} ${PAD.top + PH * 0.55},
    ${IX} ${IY}`;

/* Cost after lock: falls from center to right */
const costAfterPath = `M ${IX} ${IY}
  C ${IX + PW * 0.12} ${IY + PH * 0.08},
    ${IX + PW * 0.3} ${IY + PH * 0.2},
    ${PAD.left + PW} ${PAD.top + PH * 0.82}`;

/* Confidence: low left, inflects up sharply */
const confidenceBeforePath = `M ${PAD.left} ${PAD.top + PH * 0.42}
  C ${PAD.left + PW * 0.1} ${PAD.top + PH * 0.44},
    ${IX - PW * 0.06} ${PAD.top + PH * 0.52},
    ${IX} ${IY}`;

const confidenceAfterPath = `M ${IX} ${IY}
  C ${IX + PW * 0.1} ${IY - PH * 0.08},
    ${IX + PW * 0.25} ${IY - PH * 0.28},
    ${PAD.left + PW} ${PAD.top + PH * 0.08}`;

/* Area fill under confidence curve after lock */
const confidenceAreaPath = `${confidenceAfterPath}
  L ${PAD.left + PW} ${PAD.top + PH}
  L ${IX} ${PAD.top + PH}
  Z`;

/* ═══ SVG CHALK CIRCLE (imperfect) ═══ */
const ChalkCircle = ({ cx, cy, r }) => (
  <motion.ellipse
    cx={cx} cy={cy} rx={r} ry={r * 0.85}
    fill="none" stroke={RED} strokeWidth="1.5"
    strokeDasharray="5 4"
    transform={`rotate(-3 ${cx} ${cy})`}
    initial={{ pathLength: 0, opacity: 0 }}
    whileInView={{ pathLength: 1, opacity: 0.6 }}
    viewport={{ once: true }}
    transition={{ delay: 0.8, duration: 1.2 }}
  />
);

/* ═══ ANIMATED PATH ═══ */
const AnimPath = ({ d, stroke, strokeWidth = 2, delay = 0, dash = false }) => (
  <motion.path
    d={d} fill="none" stroke={stroke} strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeDasharray={dash ? '4 6' : undefined}
    initial={{ pathLength: 0 }}
    whileInView={{ pathLength: 1 }}
    viewport={{ once: true }}
    transition={{ delay, duration: 1.2, ease: 'easeOut' }}
  />
);

/* ═══ NARRATIVE STEPS ═══ */
const STORY_RAIL = [
  {
    num: '01',
    title: 'Fragile trust',
    body: 'Confidence decays when outcomes cannot be verified. Every transaction restarts trust.',
    accent: 'trust resets every time',
  },
  {
    num: '02',
    title: 'Proof inflection',
    body: 'The first verified twin permanently changes the slope. Reusable precedent is born.',
    accent: 'first memory lock',
  },
  {
    num: '03',
    title: 'Compounding certainty',
    body: 'Every verified outcome teaches the next decision. Proof reduces cost and compounds confidence.',
    accent: 'institutional memory',
  },
];

export default function TrustCompoundingGraph() {
  const graphRef = useRef(null);
  const inView = useInView(graphRef, { once: true, margin: '-60px' });

  return (
    <section className="relative overflow-hidden" style={{ background: BG }} data-testid="trust-graph-section">
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")`,
        opacity: 0.1, mixBlendMode: 'overlay',
      }} />

      <div className="relative z-[1] py-16 sm:py-24 px-6">
        <div className="max-w-[800px] mx-auto">
          {/* Header */}
          <motion.div
            className="mb-10"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] mb-4" style={{ color: `${RED}60` }}>
              The Economics of Proof
            </p>
            <h2
              className="text-2xl sm:text-3xl font-semibold tracking-[-0.03em] leading-[1.2]"
              style={{ color: 'var(--shell-text)', fontFamily: '"Inter Tight", sans-serif' }}
              data-testid="trust-graph-heading"
            >
              Before proof, every payment restarts trust.
              <br />
              <span style={{ color: RED }}>After proof, every payment gets cheaper, faster, and more certain.</span>
            </h2>
          </motion.div>

          {/* ═══ THEOREM GRAPH ═══ */}
          <motion.div
            ref={graphRef}
            className="rounded-xl p-4 sm:p-6 mb-8"
            style={{ background: 'var(--shell-surface)', border: '1px solid var(--shell-border)' }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="overflow-x-auto">
              <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 480 }}>
                {/* Subtle grid */}
                {[0.25, 0.5, 0.75].map(p => (
                  <line key={p}
                    x1={PAD.left} y1={PAD.top + PH * p}
                    x2={PAD.left + PW} y2={PAD.top + PH * p}
                    stroke="var(--shell-border)" strokeWidth="1"
                  />
                ))}

                {/* Y axis label */}
                <text x={PAD.left - 8} y={PAD.top - 12} fill="var(--shell-text-muted)" fontSize="8" fontFamily="monospace" textAnchor="end">
                  confidence / cost
                </text>

                {/* X axis label */}
                <text x={PAD.left + PW} y={PAD.top + PH + 30} fill="var(--shell-text-muted)" fontSize="8" fontFamily="monospace" textAnchor="end">
                  verified decisions
                </text>

                {/* Confidence area fill */}
                <motion.path
                  d={confidenceAreaPath}
                  fill={`${GREEN}08`}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 1.5, duration: 0.8 }}
                />

                {/* Cost of uncertainty: before lock (rising) */}
                <AnimPath d={costBeforePath} stroke={`${RED}70`} delay={0.2} />

                {/* Cost of uncertainty: after lock (falling) */}
                <AnimPath d={costAfterPath} stroke={`${RED}40`} delay={0.8} dash />

                {/* Confidence: before lock (flat/decaying) */}
                <AnimPath d={confidenceBeforePath} stroke={`${GREY}80`} delay={0.3} />

                {/* Confidence: after lock (rising sharply) */}
                <AnimPath d={confidenceAfterPath} stroke={GREEN} strokeWidth={2.5} delay={1} />

                {/* Chalk circle at inflection (FIRST MEMORY LOCK) */}
                <ChalkCircle cx={IX} cy={IY} r={22} />

                {/* Inflection node */}
                <motion.circle
                  cx={IX} cy={IY} r={5}
                  fill="#fff"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.7, type: 'spring', stiffness: 200 }}
                />
                <motion.circle
                  cx={IX} cy={IY} r={12}
                  fill="none" stroke="var(--shell-text-faint)" strokeWidth="1"
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.9, duration: 0.5 }}
                />

                {/* Inflection label (outside graph area) */}
                <motion.text
                  x={IX} y={PAD.top + PH + 18}
                  fill={RED} fontSize="8" fontFamily="'Caveat', cursive"
                  textAnchor="middle"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 0.7 }}
                  viewport={{ once: true }}
                  transition={{ delay: 1.2 }}
                >
                  first memory lock
                </motion.text>

                {/* Legend */}
                <g transform={`translate(${PAD.left + PW - 120}, ${PAD.top + 4})`}>
                  <line x1="0" y1="4" x2="16" y2="4" stroke={`${RED}70`} strokeWidth="1.5" />
                  <text x="20" y="7" fill="var(--shell-text-muted)" fontSize="7" fontFamily="monospace">cost of uncertainty</text>
                  <line x1="0" y1="16" x2="16" y2="16" stroke={GREEN} strokeWidth="2" />
                  <text x="20" y="19" fill="var(--shell-text-muted)" fontSize="7" fontFamily="monospace">verified confidence</text>
                </g>
              </svg>
            </div>
          </motion.div>

          {/* ═══ 3-STEP NARRATIVE RAIL ═══ */}
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-3 gap-4"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12 } } }}
            initial="hidden" whileInView="show" viewport={{ once: true }}
          >
            {STORY_RAIL.map((step, i) => (
              <motion.div
                key={step.num}
                className="p-4 rounded-xl"
                style={{ background: 'var(--shell-surface)', border: '1px solid var(--shell-border)' }}
                variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
                data-testid={`narrative-step-${step.num}`}
              >
                <span className="text-[9px] font-mono block mb-2" style={{ color: `${RED}60` }}>{step.num}</span>
                <h3 className="text-[14px] font-semibold mb-2" style={{ color: 'var(--shell-text-secondary)', fontFamily: '"Inter Tight", sans-serif' }}>
                  {step.title}
                </h3>
                <p className="text-[11px] leading-relaxed mb-2" style={{ color: 'var(--shell-text-muted)' }}>
                  {step.body}
                </p>
                <span className="-rotate-1 inline-block" style={{ fontFamily: '"Caveat", cursive', fontSize: 13, color: RED, opacity: 0.6 }}>
                  {step.accent}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
